import React, { useMemo } from "react";
import Chart from "react-apexcharts";

import { Box, Stack, CircularProgress } from "@mui/material";
import colors from "../../../../../utils/colors";
import { withApexChartFont, apexVerticalAreaGradient, apexNoOfYAxisTitle } from "../../../../../utils/apexChartDefaults";
import useHome from "../../../../../hooks/user/useHome";
import {
  LAYERD_SIGNATURE_WAF_LABEL,
  LAYERD_AI_SMART_PARSE_LABEL,
  alignWafDetectionTimeSeries,
} from "../../detection/wafSourceLabels";
import {
  prepareOverviewSeries,
  overviewIntervalMs,
  overviewRangeMs,
} from "../overviewChartSeries";

/** Keep at least one WAF source visible when toggling legend items. */
function handleWafLegendClick(chartContext, seriesIndex) {
  const w = chartContext.w;
  const collapsedIndices = w.globals.collapsedSeriesIndices || [];
  const isHidden = collapsedIndices.includes(seriesIndex);

  if (!isHidden) {
    const visibleCount = w.globals.series.length - collapsedIndices.length;
    if (visibleCount <= 1) return;
  }

  chartContext.legend.legendHelpers.toggleDataSeries(seriesIndex, isHidden);
}

const WAFDetectionInsight = ({ sigs, smartparses }) => {
  const { time_range } = useHome();

  const series = useMemo(() => {
    const aligned = alignWafDetectionTimeSeries(sigs, smartparses);
    const { fromMs, toMs } = overviewRangeMs(time_range);
    const intervalMs = overviewIntervalMs(time_range?.period || "7d");
    return [
      {
        name: LAYERD_SIGNATURE_WAF_LABEL,
        data: prepareOverviewSeries(aligned.signature, { fromMs, toMs, intervalMs }),
      },
      {
        name: LAYERD_AI_SMART_PARSE_LABEL,
        data: prepareOverviewSeries(aligned.smartParse, { fromMs, toMs, intervalMs }),
      },
    ];
  }, [sigs, smartparses, time_range]);

  const sparseSeries = series.some((s) => (s.data?.length || 0) <= 5);

  const options = withApexChartFont({
    chart: {
      type: "area",
      stacked: false,
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: true },
      sparkline: { enabled: false },
      foreColor: "#696969",
      events: {
        legendClick: handleWafLegendClick,
      },
    },
    dataLabels: {
      enabled: false,
    },
    colors: ["rgba(255, 219, 67, 1)", "rgb(99, 113, 247)"],

    stroke: {
      curve: "smooth",
      width: 2,
    },
    fill: apexVerticalAreaGradient({
      toColor: "rgba(99, 113, 247, 0.2)",
      opacityFrom: 0.45,
      opacityTo: 0.05,
    }),
    markers: {
      size: sparseSeries ? 4 : 0,
      hover: { size: 6 },
      strokeColors: "#fff",
      strokeWidth: 2,
    },
    xaxis: {
      type: "datetime",
      labels: {
        datetimeUTC: false,
        style: { fontSize: "12px", colors: "#696969" },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tickAmount: 2,
    },
    yaxis: {
      tickAmount: 5,
      min: 0,
      forceNiceScale: true,
      title: apexNoOfYAxisTitle("No. of Detections"),
      labels: {
        style: { colors: "#696969", fontSize: "12px" },
        formatter: (val) => Math.round(val),
      },
    },
    grid: {
      show: true,
      borderColor: "#e6e6e6",
      strokeDashArray: 0,
      padding: { left: 8 },
      xaxis: { lines: { show: false } },
      yaxis: {
        lines: { show: true },
      },
    },
    legend: {
      show: true,
      fontSize: "12px",
      onItemClick: { toggleDataSeries: false },
    },
  });

  return sigs === null || smartparses === null ? (
    <Stack alignItems={"center"} justifyContent={"center"} sx={{ height: "100%" }}>
      <CircularProgress sx={{ color: colors.neutral[400] }} />
    </Stack>
  ) : (
    <Box sx={{ width: "100%", pr: 3 }}>
      <Chart options={options} series={series} type="area" height={250} />
    </Box>
  );
};
export default WAFDetectionInsight;
